import * as THREE from 'three'
import * as BufferGeometryUtils from 'three/addons/utils/BufferGeometryUtils.js'
import prand from 'pure-rand'
// import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js'
// import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js'
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js'
import { cloneDeep, isArray } from 'lodash-es'

import { buildNodes } from './buildNodes'
import { BLOCK_PIXELS, CHUNK_DEPTH, CHUNK_HEIGHT, CHUNK_WIDTH, dimLight, getVoxelIdx, PIXEL_SIZE } from './chunks'
import { bindTextureToAsset } from './bindTextureToAsset'

import EventEmitter from 'eventemitter3'
import { uuid } from './utils'
import { behaviors } from './behaviors'
import { makeSeed } from './makeSeed'

// const lineMaterial = new LineMaterial({ color: 0x000000, transparent: true, opacity: 0.5, linewidth: 2 })

export class Registry extends EventEmitter {
  constructor({ label, atlas, runScripts } = {}) {
    super()
    this.raw = []
    this.label = label
    this.atlas = atlas
    this.runScripts = runScripts || false
    this.icons = {
      byId: {}, // id -> asset
    }
    this.textures = {
      byId: {}, // id -> asset
    }
    this.blocks = {
      byId: {}, // id -> asset (cleaned)
      byState: {}, // state -> block
    }
    this.bodies = {
      byId: {}, // id -> asset
    }
    this.audios = {
      byId: {}, // id -> asset
    }
    this.sounds = {
      byId: {}, // id -> asset
    }
    this.scripts = {
      globals: {
        console: {
          log: (...args) => console.log(...args),
          warn: (...args) => console.warn(...args),
          error: (...args) => console.error(...args),
          time: (...args) => console.time(...args),
          timeEnd: (...args) => console.timeEnd(...args),
        },
        Math,
        // eval: undefined,
        harden: undefined,
        lockdown: undefined,
        CHUNK_WIDTH,
        CHUNK_DEPTH,
        CHUNK_HEIGHT,
        getVoxelIdx,
        prand,
        makeSeed,
      },
      evaluate: null,
      listeners: {}, // event -> Set(...fns)
      world: {
        on: (event, callback) => {
          if (!this.scripts.listeners[event]) this.scripts.listeners[event] = []
          this.scripts.listeners[event].add(callback)
        },
        off: (event, callback) => {
          if (!this.scripts.listeners[event]) return
          this.scripts.listeners[event].delete(callback)
        },
        setChunkGenerator: fn => {
          if (this.scripts.generateChunk) {
            console.warn('setChunkGenerator already set, overwriting it.')
          }
          // console.log('setChunkGenerator', fn)
          this.scripts.generateChunk = fn
          this.scripts.generateChunkCalls++
        },
      },
      generateChunk: null,
      generateChunkCalls: 0,
    }
    if (false /*globalThis.Compartment*/) {
      // only available where lockdown (chunk generator worker, node etc)
      const compartment = new Compartment(this.scripts.globals)
      this.scripts.evaluate = code => compartment.evaluate(code)
    } else {
      this.scripts.evaluate = code => {
        const names = Object.keys(this.scripts.globals)
        const vals = Object.values(this.scripts.globals)
        const factory = new Function(...names, `"use strict";\nreturn ${code.trim()};`)
        return factory(...vals)
      }
    }
  }

  get(type, id) {
    if (type === 'icon') {
      return this.icons.byId[id]
    } else if (type === 'texture') {
      return this.textures.byId[id]
    } else if (type === 'block') {
      return this.blocks.byId[id]
    } else if (type === 'body') {
      return this.bodies.byId[id]
    } else if (type === 'audio') {
      return this.audios.byId[id]
    } else if (type === 'sound') {
      return this.sounds.byId[id]
    } else if (type === 'script') {
      return this.scripts.byId[id]
    }
  }

  deserialize(assets) {
    // console.time('Registry.deserialize')
    this.raw = []
    this.atlas?.clear()
    this.icons.byId = {}
    this.textures.byId = {}
    this.blocks.byId = {}
    this.blocks.byState = {}
    this.bodies.byId = {}
    this.audios.byId = {}
    this.sounds.byId = {}
    this.scripts.listeners = {}
    this.scripts.generateChunk = null
    for (const asset of assets) {
      this.upsert(asset, true)
    }
    this.emit('change', { type: 'deserialize', assets })
    // console.timeEnd('Registry.deserialize')
    return this
  }

  serialize() {
    return this.raw
  }

  ops(ops) {
    // todo: ops.remove ignored, does it matter?
    if (ops.remove) {
      for (const item of ops.remove) {
        this.remove(item.type, item.id)
      }
    }
    if (ops.upsert) {
      for (const asset of ops.upsert) {
        this.upsert(asset, true)
      }
    }
    this.emit('change', { type: 'ops', ops })
  }

  apply(change) {
    if (change.type === 'deserialize') {
      this.deserialize(change.assets)
    } else if (change.type === 'upsert') {
      this.upsert(change.asset)
    } else if (change.type === 'ops') {
      this.ops(change.ops)
    }
  }

  remove(type, id) {
    const idx = this.raw.findIndex(a => a.type === type && a.id === id)
    if (idx !== -1) {
      this.raw.splice(idx, 1)
    }
    if (type === 'icon') {
      delete this.icons.byId[id]
    } else if (type === 'texture') {
      delete this.textures.byId[id]
    } else if (type === 'block') {
      delete this.blocks.byId[id]
      this.clearBlockStates(id)
    } else if (type === 'body') {
      delete this.bodies.byId[id]
    } else if (type === 'audio') {
      delete this.audios.byId[id]
    } else if (type === 'sound') {
      delete this.sounds.byId[id]
    } else if (type === 'script') {
      console.error('todo: Registry.remove script!')
    }
  }

  upsert(asset, silent) {
    // if (this.label === 'chunk-builder.worker.js') {
    // console.log('upsert', asset, this)
    // }

    // remove any previous with same type+id
    const idx = this.raw.findIndex(a => a.type === asset.type && a.id === asset.id)
    if (idx !== -1) {
      this.raw.splice(idx, 1)
    }

    // keep raw original for serialization
    this.raw.push(asset)

    if (asset.type === 'icon') {
      this.upsertIcon(asset)
    } else if (asset.type === 'texture') {
      this.upsertTexture(asset)
    } else if (asset.type === 'block') {
      this.upsertBlock(asset)
    } else if (asset.type === 'body') {
      this.upsertBody(asset)
    } else if (asset.type === 'audio') {
      this.upsertAudio(asset)
    } else if (asset.type === 'sound') {
      this.upsertSound(asset)
    } else if (asset.type === 'script') {
      this.upsertScript(asset)
    }
    if (!silent) {
      this.emit('change', { type: 'upsert', asset })
    }
  }

  upsertIcon(asset) {
    this.icons.byId[asset.id] = asset
  }

  upsertTexture(asset) {
    this.textures.byId[asset.id] = asset
    // ensure all models using this texture are bound
    if (this.atlas) {
      for (const id in this.blocks.byId) {
        const block = this.blocks.byId[id]
        if (block.model.texture === asset.id) {
          bindTextureToAsset(asset, block, this.atlas)
        }
      }
    }
  }

  upsertBlock(asset) {
    asset = cloneDeep(asset)

    // no hitbox? add a default one, so it can always be broken (air is the ONLY exception)
    // todo: enforce in editor/api so we dont need the "air" check hack
    const needsHitbox = asset.id !== 'air' && !asset.model.root.children.find(n => n.type === 'hitbox')
    if (needsHitbox) {
      asset.model.root.children.push({
        id: '$hitbox',
        name: 'hitbox',
        type: 'hitbox',
        position: [0, 16, 0],
        size: [32, 32, 32],
        children: [],
      })
    }

    // check if we can bind this blocks texture yet (if its already upserted)
    if (this.atlas) {
      const texture = this.textures.byId[asset.model.texture]
      if (texture) {
        bindTextureToAsset(texture, asset, this.atlas)
      }
    }

    // push textures to atlas and get face tileIds
    // note: this is done before we convert to world pixel size
    // if (this.atlas) {
    //   pushAssetToAtlas(asset, this.atlas)
    // }

    this.blocks.byId[asset.id] = asset

    // if we're replacing, invalidate all previous blocks
    this.clearBlockStates(asset.id)

    return true
  }

  upsertBody(asset) {
    this.bodies.byId[asset.id] = asset
  }

  upsertAudio(asset) {
    this.audios.byId[asset.id] = asset
  }

  upsertSound(asset) {
    this.sounds.byId[asset.id] = asset
  }

  upsertScript(asset) {
    if (!this.runScripts) return
    const code = wrapScript(asset.code, asset.id)
    const exec = this.scripts.evaluate(code)
    this.scripts.generateChunkCalls = 0
    exec(this.scripts.world)
    if (this.scripts.generateChunkCalls > 0) {
      this.emit('generateChunkChanged')
    }
  }

  clearBlockStates(id) {
    const statesToRemove = new Set()
    for (const state in this.blocks.byState) {
      const block = this.blocks.byState[state]
      if (block.asset.id === id) {
        statesToRemove.add(state)
      }
    }
    for (const state of statesToRemove) {
      delete this.blocks.byState[state]
    }
  }

  getBlockByState(state) {
    // check cached
    let block = this.blocks.byState[state]
    if (block) return block
    // otherwise build
    const { id, props } = decodeState(state)
    let asset = this.blocks.byId[id]
    if (!asset) {
      console.error(`unknown block: ${id}`)
      asset = this.blocks.byId.unknown
      if (!asset) return null // no unknown block? or maybe havent deserialized yet
      asset = cloneDeep(asset)
      asset.id = id // give it our id so we can clear it properly in clearBlockStates()
    }
    block = createBlock(cloneDeep(asset), props, state) // cloned because behaviors can mod
    // cache it
    this.blocks.byState[state] = block
    return block
  }

  prepareSound(sound, location) {
    if (!sound) return null
    if (!sound.variants.length) return null
    // select weighted variant
    let totalWeight = 0
    for (const v of sound.variants) {
      totalWeight += v.weight ?? 1
    }
    let r = Math.random() * totalWeight
    let chosen = sound.variants[0]
    for (const v of sound.variants) {
      r -= v.weight ?? 1
      if (r <= 0) {
        chosen = v
        break
      }
    }
    if (!chosen.id) return null
    // variant config
    let volume = chosen.volume ?? 1
    let pitch = chosen.pitch ?? 1
    // randomizations
    if (sound.volumeRandom && sound.volumeRandom > 0) {
      const delta = (Math.random() * 2 - 1) * sound.volumeRandom // -random .. +random
      volume *= 1 + delta
    }
    if (sound.pitchRandom && sound.pitchRandom > 0) {
      const delta = (Math.random() * 2 - 1) * sound.pitchRandom
      pitch *= 1 + delta
    }
    // return final sound
    return {
      id: chosen.id,
      location: sound.spatial ? location : null,
      volume,
      pitch,
    }
  }
}

function createBlock(asset, props, state) {
  // behaviors
  for (const key in asset.behaviors) {
    behaviors[key]?.onBlockBuild?.(asset, props)
  }

  // convert from unit size to world pixel size
  // this is done here so behaviors earlier can work with nice unit sizes
  function unitToWorldSize(node) {
    node.position = node.position.map(p => p * PIXEL_SIZE)
    if (node.size) {
      node.rawSize = node.size.slice() // keep raw pixel size around for bindTextureToAsset
      node.size = node.size.map(s => s * PIXEL_SIZE)
    }
    for (const child of node.children) {
      unitToWorldSize(child)
    }
  }
  unitToWorldSize(asset.model.root)

  const { faces, collider, hitbox } = buildNodes(asset.model.root, props)

  let _hitbox
  let _damageModel

  return {
    id: asset.id,

    asset,
    props,
    state,

    fullOpaque: asset.opaque.every(v => !!v),
    fullSolid: asset.solid.every(v => !!v),

    faces,
    collider, // { position, index }

    get hitbox() {
      if (!_hitbox) {
        const position = new Float32Array(hitbox.position.length)
        for (let i = 0; i < hitbox.position.length; i++) {
          position[i] = hitbox.position[i]
        }
        const index = new Uint32Array(hitbox.index.length)
        for (let i = 0; i < hitbox.index.length; i++) {
          index[i] = hitbox.index[i]
        }
        const geometry = new THREE.BufferGeometry()
        geometry.setAttribute('position', new THREE.BufferAttribute(position, 3))
        geometry.setIndex(new THREE.BufferAttribute(index, 1))
        geometry.computeBoundsTree()

        // console.time('createOutlineGeometry')
        const geometryOutline = createOutlineGeometry(geometry)
        const lineGeometry = new LineSegmentsGeometry()
        lineGeometry.setPositions(geometryOutline.attributes.position.array)
        // console.timeEnd('createOutlineGeometry')

        _hitbox = {
          geometry,
          lineGeometry,
        }
      }
      return _hitbox
    },

    get damageModel() {
      if (!_damageModel) {
        let position = []
        let index = []
        let uv = []
        let wind = []
        for (const face of faces) {
          const baseIdx = position.length / 3
          position.push(...face.position)
          for (const idx of face.index) {
            index.push(baseIdx + idx)
          }
          uv.push(...face.uv)
          wind.push(...face.wind)
        }
        const positionArr = new Float32Array(position.length)
        for (let i = 0; i < position.length; i++) {
          positionArr[i] = position[i]
        }
        const indexArr = new Uint32Array(index.length)
        for (let i = 0; i < index.length; i++) {
          indexArr[i] = index[i]
        }
        const uvArr = new Uint8Array(uv.length)
        for (let i = 0; i < uv.length; i++) {
          uvArr[i] = uv[i]
        }
        const windArr = new Uint8Array(wind.length)
        for (let i = 0; i < wind.length; i++) {
          windArr[i] = wind[i]
        }
        const geometry = new THREE.BufferGeometry()
        geometry.setAttribute('position', new THREE.BufferAttribute(positionArr, 3))
        geometry.setAttribute('uv', new THREE.BufferAttribute(uvArr, 2))
        geometry.setAttribute('wind', new THREE.BufferAttribute(windArr, 1))
        geometry.setIndex(new THREE.BufferAttribute(indexArr, 1))
        _damageModel = {
          geometry,
        }
      }
      return _damageModel
    },

    solid: asset.solid,
    opaque: asset.opaque,
    attenuation: asset.attenuation,
    light: asset.light,
    replaceable: asset.replaceable,
    dimming: asset.dimming,
  }
}

const STATE_REGEX = /^([^[]+)(?:\[(.+)\])?$/

export function decodeState(str) {
  // match base id and optional [props]
  const match = str.match(STATE_REGEX)
  if (!match) return null // invalid input
  const id = match[1]
  const propsString = match[2]
  const props = {}
  if (propsString) {
    for (const pair of propsString.split(',')) {
      const [key, value] = pair.split('=')
      if (key && value) props[key.trim()] = value.trim()
    }
  }
  return { id, props }
}

export function encodeState({ id, props }) {
  if (!id) return null
  const entries = Object.entries(props || {})
  if (entries.length === 0) {
    return id
  }
  // sort keys to ensure deterministic ordering
  entries.sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
  const propsString = entries.map(([key, value]) => `${key}=${value}`).join(',')
  return `${id}[${propsString}]`
}

function wrapScript(code, name) {
  return `
(function() {
  return (world) => {
    ${code}
  }
})()
//# sourceURL=${name}.js
  `
}

function createOutlineGeometry(geometry) {
  const positions = geometry.attributes.position.array
  const indices = geometry.index ? geometry.index.array : null

  if (!indices) {
    console.error('Geometry must be indexed')
    return new THREE.BufferGeometry()
  }

  // Build vertex deduplication map
  const vertexMap = new Map()
  const vertexRemap = new Array(positions.length / 3)
  const precision = 1000

  for (let i = 0; i < positions.length / 3; i++) {
    const x = positions[i * 3]
    const y = positions[i * 3 + 1]
    const z = positions[i * 3 + 2]

    const key = `${Math.round(x * precision)},${Math.round(y * precision)},${Math.round(z * precision)}`

    if (!vertexMap.has(key)) {
      vertexMap.set(key, i)
    }

    vertexRemap[i] = vertexMap.get(key)
  }

  // Build edge map
  const edges = new Map()

  const getEdgeKey = (a, b) => {
    a = vertexRemap[a]
    b = vertexRemap[b]
    return a < b ? `${a}_${b}` : `${b}_${a}`
  }

  const addEdge = (a, b, normal) => {
    const key = getEdgeKey(a, b)
    if (!edges.has(key)) {
      edges.set(key, { indices: [a, b], normals: [] })
    }
    edges.get(key).normals.push(normal)
  }

  // Process each triangle
  for (let i = 0; i < indices.length; i += 3) {
    const i0 = indices[i]
    const i1 = indices[i + 1]
    const i2 = indices[i + 2]

    const v0 = new THREE.Vector3(positions[i0 * 3], positions[i0 * 3 + 1], positions[i0 * 3 + 2])
    const v1 = new THREE.Vector3(positions[i1 * 3], positions[i1 * 3 + 1], positions[i1 * 3 + 2])
    const v2 = new THREE.Vector3(positions[i2 * 3], positions[i2 * 3 + 1], positions[i2 * 3 + 2])

    const edge1 = new THREE.Vector3().subVectors(v1, v0)
    const edge2 = new THREE.Vector3().subVectors(v2, v0)
    const normal = new THREE.Vector3().crossVectors(edge1, edge2).normalize()

    addEdge(i0, i1, normal)
    addEdge(i1, i2, normal)
    addEdge(i2, i0, normal)
  }

  // Build list of outline edge positions
  const outlinePositions = []
  const thresholdCoplanar = 0.99
  const thresholdOpposite = -0.99

  edges.forEach(edgeData => {
    const {
      indices: [a, b],
      normals,
    } = edgeData

    let isOutline = false

    if (normals.length === 1) {
      // Boundary edge
      isOutline = true
    } else {
      // Cancel out opposite normals first
      const activeNormals = []
      const used = new Array(normals.length).fill(false)

      for (let i = 0; i < normals.length; i++) {
        if (used[i]) continue

        let foundOpposite = false
        for (let j = i + 1; j < normals.length; j++) {
          if (used[j]) continue

          const dot = normals[i].dot(normals[j])
          if (dot < thresholdOpposite) {
            // These are opposite normals - they cancel out
            used[i] = true
            used[j] = true
            foundOpposite = true
            break
          }
        }

        if (!foundOpposite) {
          activeNormals.push(normals[i])
        }
      }

      // Now check the remaining active normals
      if (activeNormals.length === 0) {
        // All normals cancelled out - internal edge
        isOutline = false
      } else if (activeNormals.length === 1) {
        // One normal left (the others cancelled) - boundary
        isOutline = true
      } else {
        // Multiple normals - check if any are non-coplanar
        let hasAngle = false
        for (let i = 0; i < activeNormals.length; i++) {
          for (let j = i + 1; j < activeNormals.length; j++) {
            const dot = activeNormals[i].dot(activeNormals[j])
            if (dot < thresholdCoplanar) {
              hasAngle = true
              break
            }
          }
          if (hasAngle) break
        }
        isOutline = hasAngle
      }
    }

    if (isOutline) {
      outlinePositions.push(
        positions[a * 3],
        positions[a * 3 + 1],
        positions[a * 3 + 2],
        positions[b * 3],
        positions[b * 3 + 1],
        positions[b * 3 + 2]
      )
    }
  })

  const outlineGeometry = new THREE.BufferGeometry()
  outlineGeometry.setAttribute('position', new THREE.Float32BufferAttribute(outlinePositions, 3))

  return outlineGeometry
}
